import { Box, Typography, useTheme, Button, InputBase } from "@mui/material";
import { useSelector }  from "react-redux";
import { useState }     from "react";

import ApartmentIcon     from "@mui/icons-material/Apartment";
import SearchIcon        from "@mui/icons-material/Search";
import AddIcon           from "@mui/icons-material/Add";
import EditOutlinedIcon  from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import ReportTable, { RRow, RCell } from "components/Tables/ReportTable";
import FixedSizeDialog   from "components/fixedSizeDialog";
import ResponsiveDialog  from "components/responsiveDialog";
import DepartmentCreateForm from "../forms/departmentForms/departmentCreateForm";
import DepartmentEditForm   from "../forms/departmentForms/departmentEditForm";
import { useDepartmentsList } from "api/hooks/humanResource";
import { requester }     from "api/pesabackend";

export default function DepartmentTable({ setIsSuccess }) {
  const theme  = useTheme();
  const isDark = theme.palette.mode === "dark";

  const bodyBg  = theme.palette.background.alt;
  const cardBg  = isDark ? "rgba(255,255,255,0.03)" : "#ffffff";
  const accent  = theme.palette.secondary.main;
  const textPri = theme.palette.neutral.dark;
  const textSec = theme.palette.neutral.main;
  const border  = isDark ? "rgba(29,158,117,0.14)" : "rgba(29,158,117,0.16)";

  const business_branch_id = useSelector((s) => s.authReducer.business_branch_id);
  const { departmentList, isLoadingDepartmentList, setSearchDepartments, setNewDepartmentData } = useDepartmentsList(1, business_branch_id);

  const [open,        setOpen]        = useState(false);
  const [openEdit,    setOpenEdit]    = useState(false);
  const [openDelete,  setOpenDelete]  = useState(false);
  const [editData,    setEditData]    = useState("");
  const [clickedItem, setClickedItem] = useState("");
  const [search,      setSearch]      = useState("");

  const handleClickEditOpen = async (row) => {
    const res = await requester.get(`/human_resource/departments/get/${row.id}`);
    setEditData(res.data.data);
    setOpenEdit(true);
  };

  const handleClickDelete = async () => {
    await requester.post(`/human_resource/departments/delete?id=${clickedItem.id}`);
    setIsSuccess(true); setNewDepartmentData(true); setOpenDelete(false);
  };

  const columns = [
    { label: "#",               width: 40 },
    { label: "Department Name"            },
    { label: "Actions",        align: "center" },
  ];

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: bodyBg, p: "1.5rem 2rem", display: "flex", flexDirection: "column", gap: "1.5rem" }}>

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "1rem" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <Box sx={{ width: "40px", height: "40px", borderRadius: "10px", backgroundColor: "rgba(29,158,117,0.12)", display: "flex", alignItems: "center", justifyContent: "center", color: accent, flexShrink: 0 }}>
            <ApartmentIcon sx={{ fontSize: "22px" }} />
          </Box>
          <Box>
            <Typography sx={{ fontFamily: "'DM Serif Display', serif", fontSize: "22px", fontWeight: 400, color: textPri, lineHeight: 1.2 }}>Departments</Typography>
            <Typography sx={{ fontSize: "12px", color: textSec, mt: "2px" }}>{departmentList.length} departments</Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "6px", px: "0.75rem", height: "36px", backgroundColor: cardBg, border: `1px solid ${border}`, borderRadius: "8px", minWidth: "200px" }}>
            <SearchIcon sx={{ fontSize: "16px", color: textSec }} />
            <InputBase placeholder="Search department…" value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") setSearchDepartments(search); }} sx={{ flex: 1, fontSize: "13px", color: textPri }} />
            <Box onClick={() => setSearchDepartments(search)} sx={{ fontSize: "11px", fontWeight: 500, color: accent, cursor: "pointer", "&:hover": { textDecoration: "underline" } }}>Search</Box>
          </Box>
          <Button onClick={() => setOpen(true)} startIcon={<AddIcon sx={{ fontSize: "16px" }} />}
            sx={{ fontSize: "13px", fontWeight: 500, px: "1rem", py: "0.45rem", borderRadius: "8px", backgroundColor: accent, color: "#fff", textTransform: "none", "&:hover": { backgroundColor: "#0F6E56" } }}>
            Add Department
          </Button>
        </Box>
      </Box>

      <ReportTable columns={columns} rows={departmentList} loading={isLoadingDepartmentList}
        emptyMessage="No departments found"
        renderRow={(row, index) => (
          <RRow key={row.id} index={index}>
            <RCell>{index + 1}</RCell>
            <RCell><Typography sx={{ fontSize: "13px", fontWeight: 500, color: textPri }}>{row.department_name}</Typography></RCell>
            <RCell align="center">
              <Box sx={{ display: "flex", gap: "5px", justifyContent: "center" }}>
                <Box onClick={() => handleClickEditOpen(row)} sx={{ display: "inline-flex", alignItems: "center", gap: "4px", px: "8px", py: "4px", borderRadius: "6px", border: "1px solid rgba(186,117,23,0.3)", cursor: "pointer", color: "#BA7517", fontSize: "12px", fontWeight: 500, "&:hover": { backgroundColor: "rgba(186,117,23,0.08)" } }}>
                  <EditOutlinedIcon sx={{ fontSize: "14px" }} /> Edit
                </Box>
                <Box onClick={() => { setClickedItem(row); setOpenDelete(true); }} sx={{ display: "inline-flex", alignItems: "center", gap: "4px", px: "8px", py: "4px", borderRadius: "6px", border: "1px solid rgba(226,75,74,0.3)", cursor: "pointer", color: "#e24b4a", fontSize: "12px", fontWeight: 500, "&:hover": { backgroundColor: "rgba(226,75,74,0.08)" } }}>
                  <DeleteOutlineIcon sx={{ fontSize: "14px" }} /> Delete
                </Box>
              </Box>
            </RCell>
          </RRow>
        )}
      />

      <ResponsiveDialog open={open} handleClickOpen={() => setOpen(true)} handleClose={() => setOpen(false)} dialogHeading="Add a New Department" negativeResponse="Cancel"
        content={<Box p="1rem" borderRadius="0.5rem" backgroundColor={theme.palette.background.alt}><DepartmentCreateForm setIsSuccess={setIsSuccess} setOpen={setOpen} setNewDepartmentData={setNewDepartmentData} /></Box>}
      />
      <ResponsiveDialog open={openEdit} handleClose={() => setOpenEdit(false)} dialogHeading="Edit Department" negativeResponse="Cancel"
        content={<Box p="1rem" borderRadius="0.5rem" backgroundColor={theme.palette.background.alt}><DepartmentEditForm setOpen={setOpenEdit} setNewDepartmentData={setNewDepartmentData} data={editData} setIsSuccess={setIsSuccess} /></Box>}
      />
      <FixedSizeDialog open={openDelete} handleClose={() => setOpenDelete(false)} dialogHeading={`Delete ${clickedItem.department_name}?`} negativeResponse="Cancel"
        positiveResponse="Yes, delete" handleSubmit={handleClickDelete} maxWidth="sm"
        content={
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: "10px", backgroundColor: "rgba(226,75,74,0.07)", border: "1px solid rgba(226,75,74,0.2)", borderRadius: "8px", p: "0.875rem 1rem" }}>
            <DeleteOutlineIcon sx={{ fontSize: "18px", color: "#e24b4a", flexShrink: 0 }} />
            <Typography sx={{ fontSize: "13px", color: isDark ? "#f09595" : "#a32d2d", lineHeight: 1.6 }}>
              This will permanently delete the <strong>{clickedItem.department_name}</strong> department.
            </Typography>
          </Box>
        }
      />
    </Box>
  );
}